import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Button, Input, Space, Tabs, Tooltip, Typography, message, Popconfirm } from 'antd';
import { SaveOutlined, ReloadOutlined, DeleteOutlined, UndoOutlined, RedoOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { metadataApi } from '@api/metadataApi';
import type { MetaDocumentTypeDto } from '@/types/metadata';
import type { FormLayout } from '@api/importApi';
import LayoutTreeEditor from './LayoutTreeEditor';

const { Text, Paragraph } = Typography;

interface Props {
  acqType: MetaDocumentTypeDto;
}

/**
 * Двотабовий редактор LayoutJson:
 *   • Drag-drop — візуальне дерево вузлів через @dnd-kit/sortable + property panel
 *   • JSON     — необроблений текстовий редактор (fallback / advanced)
 *
 * Обидва таби читають/пишуть один і той же `acqType.layoutJson`. Локальний
 * стейт `acqLayout` синхронізується з parent через debounced save (явним кліком).
 */
export default function LayoutEditor({ acqType }: Props) {
  const acqQc = useQueryClient();
  const [acqLayout, setAcqLayout] = useState<FormLayout | null>(parseLayout(acqType.layoutJson));
  const [acqText, setAcqText] = useState<string>(formatJson(acqType.layoutJson));
  const [acqError, setAcqError] = useState<string | null>(null);
  const [acqDirty, setAcqDirty] = useState(false);

  // ── Undo/redo: тримаємо стек попередніх станів. Лінійна історія —
  // нова зміна після undo обрізає redo-stack.
  const acqUndoStack = useRef<(FormLayout | null)[]>([]);
  const acqRedoStack = useRef<(FormLayout | null)[]>([]);
  const [acqHistoryTick, setAcqHistoryTick] = useState(0); // примусове перерендерення кнопок

  const acqPushHistory = useCallback((acqPrev: FormLayout | null) => {
    acqUndoStack.current.push(acqPrev);
    if (acqUndoStack.current.length > 50) acqUndoStack.current.shift(); // ліміт
    acqRedoStack.current = [];
    setAcqHistoryTick((t) => t + 1);
  }, []);

  const acqUndo = useCallback(() => {
    if (acqUndoStack.current.length === 0) return;
    const acqPrev = acqUndoStack.current.pop()!;
    acqRedoStack.current.push(acqLayout);
    setAcqLayout(acqPrev);
    setAcqText(acqPrev ? JSON.stringify(acqPrev, null, 2) : '');
    setAcqDirty(true);
    setAcqHistoryTick((t) => t + 1);
  }, [acqLayout]);

  const acqRedo = useCallback(() => {
    if (acqRedoStack.current.length === 0) return;
    const acqNext = acqRedoStack.current.pop()!;
    acqUndoStack.current.push(acqLayout);
    setAcqLayout(acqNext);
    setAcqText(acqNext ? JSON.stringify(acqNext, null, 2) : '');
    setAcqDirty(true);
    setAcqHistoryTick((t) => t + 1);
  }, [acqLayout]);

  // Hot keys Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z
  useEffect(() => {
    const acqHandler = (e: KeyboardEvent) => {
      const acqIsCtrl = e.ctrlKey || e.metaKey;
      if (!acqIsCtrl) return;
      const acqTarget = e.target as HTMLElement;
      // Не перехоплюємо у textarea/input — там потрібен native undo
      if (acqTarget && (acqTarget.tagName === 'TEXTAREA' || acqTarget.tagName === 'INPUT' || acqTarget.isContentEditable)) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); acqUndo(); }
      else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); acqRedo(); }
    };
    window.addEventListener('keydown', acqHandler);
    return () => window.removeEventListener('keydown', acqHandler);
  }, [acqUndo, acqRedo]);

  useEffect(() => {
    setAcqLayout(parseLayout(acqType.layoutJson));
    setAcqText(formatJson(acqType.layoutJson));
    setAcqDirty(false);
    acqUndoStack.current = [];
    acqRedoStack.current = [];
    setAcqHistoryTick(0);
  }, [acqType.id, acqType.layoutJson]);

  const acqSave = useMutation({
    mutationFn: (json: string | null) => metadataApi.setLayoutJson(acqType.id, json),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types', acqType.code] });
      message.success('Layout збережено');
      setAcqError(null);
      setAcqDirty(false);
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.detail ?? e?.response?.data ?? e.message;
      setAcqError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      message.error('Помилка збереження');
    },
  });

  const acqHandleSaveJson = () => {
    if (!acqText.trim()) { acqSave.mutate(null); return; }
    try { JSON.parse(acqText); } catch (e: any) {
      setAcqError(`Invalid JSON: ${e.message}`);
      return;
    }
    setAcqError(null);
    acqSave.mutate(acqText);
  };

  const acqHandleSaveTree = () => {
    if (!acqLayout) { acqSave.mutate(null); return; }
    acqSave.mutate(JSON.stringify(acqLayout));
  };

  const acqResetToFallback = () => {
    setAcqText('');
    setAcqLayout(null);
    acqSave.mutate(null);
  };

  // Sync між табами: коли drag-drop редактор міняє layout — пушимо старе у undo-stack і оновлюємо
  const acqHandleTreeChange = (acqNew: FormLayout) => {
    acqPushHistory(acqLayout);
    setAcqLayout(acqNew);
    setAcqText(JSON.stringify(acqNew, null, 2));
    setAcqDirty(true);
  };

  const acqHandleJsonChange = (s: string) => {
    setAcqText(s);
    setAcqError(null);
    setAcqDirty(true);
    try {
      const acqParsed = JSON.parse(s);
      if (acqParsed && typeof acqParsed === 'object') {
        setAcqLayout(acqParsed as FormLayout);
      }
    } catch { /* JSON ще не valid — drag-drop таб покаже останній валідний */ }
  };

  const acqIsCompact = !acqType.layoutJson;

  return (
    <div>
      <Alert
        type={acqIsCompact ? 'warning' : 'info'} showIcon style={{ marginBottom: 12 }}
        message={acqIsCompact
          ? 'Layout не задано — BasFormRenderer використовує синтезований fallback (Шапка + Реквізити + Таблиці).'
          : 'Layout заданий явно (з BAS Phase 0 або вручну). Редагуй візуально (Drag-drop) або у JSON.'}
      />

      {acqError && (
        <Alert type="error" message={acqError} closable onClose={() => setAcqError(null)} style={{ marginBottom: 12 }} />
      )}

      <Tabs
        defaultActiveKey="tree"
        items={[
          {
            key: 'tree',
            label: 'Drag-drop',
            children: (
              <>
                <LayoutTreeEditor
                  acqLayout={acqLayout}
                  acqAttributes={acqType.attributes}
                  acqOnChange={acqHandleTreeChange}
                />
                <Space style={{ marginTop: 12 }}>
                  <Button type="primary" icon={<SaveOutlined />} loading={acqSave.isPending} onClick={acqHandleSaveTree} disabled={!acqDirty}>
                    Зберегти
                  </Button>
                  <Tooltip title="Undo (Ctrl+Z)">
                    <Button
                      icon={<UndoOutlined />}
                      disabled={acqUndoStack.current.length === 0}
                      onClick={acqUndo}
                      data-history-tick={acqHistoryTick}
                    />
                  </Tooltip>
                  <Tooltip title="Redo (Ctrl+Y)">
                    <Button
                      icon={<RedoOutlined />}
                      disabled={acqRedoStack.current.length === 0}
                      onClick={acqRedo}
                    />
                  </Tooltip>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => { setAcqLayout(parseLayout(acqType.layoutJson)); setAcqText(formatJson(acqType.layoutJson)); setAcqDirty(false); acqUndoStack.current = []; acqRedoStack.current = []; setAcqHistoryTick((t) => t + 1); }}
                    disabled={!acqDirty}
                  >
                    Скинути зміни
                  </Button>
                  <Popconfirm title="Стерти LayoutJson?" description="Перейде на synthesized fallback." onConfirm={acqResetToFallback}>
                    <Button danger icon={<DeleteOutlined />} disabled={!acqType.layoutJson}>Скинути до fallback</Button>
                  </Popconfirm>
                </Space>
              </>
            ),
          },
          {
            key: 'json',
            label: 'JSON (advanced)',
            children: (
              <>
                <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
                  Структура: <Text code>{`{ nodes: [FormNode] }`}</Text>, де FormNode має <Text code>type</Text>:
                  <Text code>group</Text> / <Text code>field</Text> / <Text code>checkbox</Text> /
                  <Text code>label</Text> / <Text code>pages</Text> / <Text code>page</Text> / <Text code>table</Text>.
                </Paragraph>
                <Input.TextArea
                  value={acqText}
                  onChange={(e) => acqHandleJsonChange(e.target.value)}
                  rows={20}
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                  placeholder='{"nodes":[...]}'
                />
                <Space style={{ marginTop: 12 }}>
                  <Button type="primary" icon={<SaveOutlined />} loading={acqSave.isPending} onClick={acqHandleSaveJson}>
                    Зберегти JSON
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => { setAcqText(formatJson(acqType.layoutJson)); setAcqLayout(parseLayout(acqType.layoutJson)); setAcqDirty(false); }}
                    disabled={!acqDirty}
                  >
                    Скинути зміни
                  </Button>
                </Space>
              </>
            ),
          },
        ]}
      />
    </div>
  );
}

function formatJson(s: string | null | undefined): string {
  if (!s) return '';
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

function parseLayout(s: string | null | undefined): FormLayout | null {
  if (!s) return null;
  try {
    const acqObj = JSON.parse(s);
    if (acqObj && typeof acqObj === 'object' && Array.isArray(acqObj.nodes)) {
      return acqObj as FormLayout;
    }
  } catch { /* invalid JSON — таб JSON покаже текст як є */ }
  return null;
}
